/**
 * Validation for the "push a version" form.
 *
 * Kept apart from the component because these are the rules the BFF enforces, and getting them
 * wrong shows up as a 409 after the fact instead of as a message under the field.
 *
 * @module Pages/BUs/BUManager
 */

import type { PlatformVersion } from '../Components/BUPlatformList/BUPlatformList.types';

/** `major.minor`, the shape every tenant version in the BFF uses (1.0, 2.7, 3.12). */
const VERSION_PATTERN = /^\d+\.\d+$/;

/** `AAAA-MM-DD`, the only release-date shape the BFF accepts. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface VersionFormValues {
  version: string;
  name: string;
  releasedAt: string;
}

export interface VersionFormErrors {
  version?: string;
  releasedAt?: string;
}

/**
 * Validates the form against the rules the BFF applies.
 *
 * @param values - What is typed in the form.
 * @param existing - Versions already registered for the BU; a repeat is a 409 on the server.
 */
export function validateVersionForm(
  values: VersionFormValues,
  existing: PlatformVersion[],
): VersionFormErrors {
  const errors: VersionFormErrors = {};
  const version = values.version.trim();

  if (version.length === 0) {
    errors.version = 'Informe a versão.';
  } else if (!VERSION_PATTERN.test(version)) {
    errors.version = 'Use o formato major.minor, por exemplo 1.0 ou 2.7.';
  } else {
    const clash = existing.find(entry => entry.version === version);

    if (clash) {
      /*
        A disabled version still owns its number: the unique key is (tenant, version) and ignores
        `isActive`, so pushing it again is a 409 — and until this said so, it was a 409 about a
        version that is not in the list.
      */
      errors.version = clash.isActive
        ? 'Esta versão já está registrada nesta BU.'
        : 'Esta versão existe nesta BU, desativada. Reative-a na lista em vez de registrar de novo.';
    }
  }

  const releasedAt = values.releasedAt.trim();

  if (releasedAt.length > 0) {
    if (!DATE_PATTERN.test(releasedAt)) {
      errors.releasedAt = 'Use o formato AAAA-MM-DD.';
    } else if (Number.isNaN(new Date(`${releasedAt}T00:00:00`).getTime())) {
      errors.releasedAt = 'Esta data não existe.';
    }
  }

  return errors;
}

/** True when nothing is wrong and the form may be submitted. */
export function isVersionFormValid(errors: VersionFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/**
 * The date shown beside a version.
 *
 * Release dates arrive as ISO instants. Formatting them in the local timezone would show a version
 * released on the 1st as the 31st for anyone west of UTC, so the date part is read directly.
 */
export function formatReleaseDate(value: string | null | undefined): string {
  if (!value) return 'sem data';
  const [datePart] = value.split('T');
  return DATE_PATTERN.test(datePart ?? '') ? (datePart as string) : 'sem data';
}
